import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { concat, Observable, of } from 'rxjs';
import { catchError, filter, last, map, mergeMap, switchMap, take } from 'rxjs/operators';
import { DocumentTypeLevelType } from 'src/app/compliance/api/enums/document-type-level-type.enum';
import { GetRuleExpressionsInHierarchyRequestDto } from 'src/app/compliance/api/models/get-rule-expressions-in-hierarchy-request-dto.model';
import { DocumentTypeLevel } from 'src/app/compliance/enums/document-type-level.enum';
import { DocumentTypeCategory } from 'src/app/compliance/models/document-type-category.model';
import { loadAutomatchGroupedWinningRuleset } from 'src/app/compliance/store/actions/automatch.actions';
import { MicDropFeatureFlags } from 'src/app/shared/models/enums/team-feature-flags/mic-drop-feature-flags.enum';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { isEmpty } from 'src/app/shared/utilities/obj-utilities';
import { DocumentTypes } from '../../../compliance/models/document-types.model';
import { DocumentTypeActions } from '../actions';
import { selectPartnerNetworkTaskTypes } from '../doc-admin.selector';
import { DocumentAdminClient } from 'src/app/compliance/api/document-admin-client.service';
import { FacilityTypeDto } from 'src/app/compliance/api/models/facility-type-dto.model';
import { WorkTypeSettingsDto } from 'src/app/compliance/api/models/work-type-settings-dto.model';
import { LocumsContractTypeSettingsDto } from 'src/app/compliance/api/models/locums-contract-type-settings-dto.model';
import { LineOfBusinessesClient } from 'src/app/compliance/api/line-of-businesses-client.service';
import { ManualReviewCandidateTypeDto } from 'src/app/compliance/api/models/manual-review-candidate-type-dto.model';
import { DocAdminHelperService } from 'src/app/compliance/requirements-admin/doc-admin-helper.service';
import { KeyValueDto } from 'src/app/compliance/api/models/key-value-dto.model';
import { DocumentTypesClient } from 'src/app/compliance/api/document-types-client.service';
import { DocumentTypeCategoryDto } from 'src/app/compliance/api/models/document-type-category-dto.model';
import { CategoryLineOfBusinessEnum } from 'src/app/compliance/api/enums/category-line-of-business-enum.enum';
import { DocumentTypeCreateDto } from 'src/app/compliance/api/models/document-type-create-dto.model';
import { AdminDocumentDto } from 'src/app/compliance/api/models/admin-document-dto.model';
import { DocumentTypeUpdateDto } from 'src/app/compliance/api/models/document-type-update-dto.model';
@Injectable()
export class DocumentTypeDetailsEffects {
    constructor(
        private readonly actions$: Actions,
        private readonly docAdminTypeClient: DocumentTypesClient,
        private readonly docAdminClient: DocumentAdminClient,
        private readonly docAdminHelperClient: DocAdminHelperService,
        private readonly lineOfBusinessesClient: LineOfBusinessesClient,
        private readonly featureManager: LDFeatureManager,
        private readonly toasterService: ToasterService,
        private readonly store: Store<{}>
    ) {}

    loadPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getPermissions),
            switchMap((action) =>
                this.docAdminHelperClient.getPermissions().pipe(
                    map((permissions) =>
                        DocumentTypeActions.DocumentTypesDetail.getPermissionsSuccess({ permissions })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypesDetail.getPermissionsFailure({ error })))
                )
            )
        )
    );

    loadInitFacilityLookup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookup),
            switchMap((action) =>
                this.docAdminClient.getFacilityLookups(action.lob, action.id.toString()).pipe(
                    map((facilities: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupSuccess({
                            facility: facilities?.find((f) => f.id === action.id) as unknown as KeyValue<number, string>
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupFailure({ error }))
                    )
                )
            )
        )
    );

    loadFacilityLookups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getFacilityLookups),
            switchMap((action) =>
                this.docAdminClient.getFacilityLookups(action.lob, action.name).pipe(
                    map((facilities: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsSuccess({
                            facilities: facilities as unknown as KeyValue<number, string>[]
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getFacilityLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadInitAssociationLookup$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookup),
            switchMap((action) =>
                this.docAdminClient.getAssociationLookups(action.id.toString()).pipe(
                    map((associations: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getInitAssociationLookupSoccess({
                            association: associations?.find((a) => a.id === action.id) as unknown as KeyValue<
                                number,
                                string
                            >
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getInitFacilityLookupFailure({ error }))
                    )
                )
            )
        )
    );

    loadAssociationLookups$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getAssociationLookups),
            switchMap((action) =>
                this.docAdminClient.getAssociationLookups(action.name).pipe(
                    map((associations: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsSuccess({
                            associations: associations as unknown as KeyValue<number, string>[]
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getAssociationLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadCandidateTaskTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypes),
            switchMap(() =>
                this.docAdminClient.getCandidateTaskTypes(false).pipe(
                    map((candidateTaskTypes: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesSuccess({
                            candidateTaskTypes: candidateTaskTypes as unknown as KeyValue<number, string>[]
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getCandidateTaskTypesFailure({ error }))
                    )
                )
            )
        )
    );

    loadPartnerNetworkTaskTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypes),
            switchMap(() => this.store.select(selectPartnerNetworkTaskTypes).pipe(take(1))),
            filter((pntt) => (pntt ?? []).length <= 0),
            switchMap(() =>
                this.docAdminClient.getPartnerNetworkTaskTypes().pipe(
                    map((partnerNetworkTaskTypes: KeyValueDto[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesSuccess({
                            partnerNetworkTaskTypes: partnerNetworkTaskTypes as unknown as KeyValue<number, string>[]
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getPartnerNetworkTaskTypesFailure({ error }))
                    )
                )
            )
        )
    );

    loadSpecialties$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookups),
            switchMap((action) =>
                this.docAdminClient.getSpecialties(action.getLocums).pipe(
                    map((specialties: KeyValueDto[]) => {
                        const specialtiesTransformed = specialties.map((s) => ({
                            key: s.id,
                            id: s.id,
                            name: s.name
                        })) as KeyValue<number, string>[];
                        specialtiesTransformed.unshift({ key: null, name: 'All Specialties' });
                        return DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsSuccess({
                            specialties: specialtiesTransformed
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getSpecialtyLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadRequirementLevels$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookups),
            switchMap((action) =>
                this.docAdminClient.getRequirementLevels().pipe(
                    map((requirementLevels: KeyValueDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsSuccess({
                            requirementLevels: requirementLevels as unknown as KeyValue<number, string>[]
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getRequirementLevelLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadStates$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getStateLookups),
            switchMap(() =>
                this.docAdminClient.getStates().pipe(
                    map((states: KeyValueDto[]) => {
                        const statesTransformed = states as unknown as KeyValue<number, string>[];
                        statesTransformed.unshift({ key: null, name: 'All States' });
                        return DocumentTypeActions.DocumentTypesDetail.getStateLookupsSuccess({
                            states: statesTransformed
                        });
                    }),
                    catchError((error) => of(DocumentTypeActions.DocumentTypesDetail.getStateLookupsFailure({ error })))
                )
            )
        )
    );

    loadFacilityTypes$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookups),
            switchMap(() =>
                this.docAdminClient.getFacilityTypes().pipe(
                    map((facilityTypes: FacilityTypeDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsSuccess({ facilityTypes });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getFacilityTypeLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadManualReviewCandidateTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookups),
            switchMap(() =>
                this.docAdminClient.getManualReviewCandidateTypes().pipe(
                    map((manualReviewTypes: ManualReviewCandidateTypeDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsSuccess(
                            {
                                manualReviewTypes
                            }
                        );
                    }),
                    catchError((error) =>
                        of(
                            DocumentTypeActions.DocumentTypesDetail.getManualReviewCandidateTypeSettingLookupsFailure({
                                error
                            })
                        )
                    )
                )
            )
        )
    );

    loadWorkTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingsLookups),
            switchMap(() =>
                this.docAdminClient.getWorkTypeSettings().pipe(
                    map((workTypeSettings: WorkTypeSettingsDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsSuccess({
                            workTypeSettings
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getWorkTypeSettingLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadLocumsContractTypeSettings$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingsLookups),
            switchMap(() =>
                this.docAdminClient.getLocumsContractTypeSettings().pipe(
                    map((locumsContractTypeSettings: LocumsContractTypeSettingsDto[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsSuccess({
                            locumsContractTypeSettings
                        });
                    }),
                    catchError((error) =>
                        of(
                            DocumentTypeActions.DocumentTypesDetail.getLocumsContractTypeSettingLookupsFailure({
                                error
                            })
                        )
                    )
                )
            )
        )
    );

    loadLinesOfBusiness$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookups),
            switchMap(() =>
                this.lineOfBusinessesClient.all().pipe(
                    map((a) => a.map((x) => ({ key: x.id, id: x.id, name: x.name }) as KeyValue<number, string>)),
                    map((linesOfBusiness: KeyValue<number, string>[]) => {
                        return DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookupsSuccess({
                            linesOfBusiness
                        });
                    }),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getLinesOfBusinessLookupsFailure({ error }))
                    )
                )
            )
        )
    );

    loadDocumentTypeCategories$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategories),
            switchMap((action) => {
                let getDocumentTypeCategories$: Observable<string[] | DocumentTypeCategoryDto[]>;
                if (action.catLob === 0) {
                    getDocumentTypeCategories$ = this.docAdminTypeClient.getAllDocumentTypeCategories();
                } else {
                    getDocumentTypeCategories$ = this.docAdminTypeClient.getDocumentTypeCategoriesByCLob(
                        action.catLob as unknown as CategoryLineOfBusinessEnum
                    );
                }

                return getDocumentTypeCategories$.pipe(
                    map((documentTypeCatories: DocumentTypeCategory[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesSuccess({
                            documentTypeCatories
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesFailure({ error }))
                    )
                );
            })
        )
    );

    loadDocumentTypeCategoriesList$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesList),
            switchMap((action) => {
                return this.docAdminTypeClient.getAllDocumentTypeCategories().pipe(
                    map((documentTypeCategoriesList: string[]) =>
                        DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesListSuccess({
                            documentTypeCategoriesList
                        })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypesDetail.getDocumentTypeCategoriesListFailure({ error }))
                    )
                );
            })
        )
    );

    getOcrEnabledStatus$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getOcrEnabledStatus),
            switchMap((action) => {
                return this.docAdminClient.isOcrEnabled().pipe(
                    map((isOcrEnabled: boolean) =>
                        DocumentTypeActions.DocumentTypes.getOcrEnabledStatusSuccess({ isOcrEnabled })
                    ),
                    catchError((error) => of(DocumentTypeActions.DocumentTypes.getOcrEnabledStatusFailure({ error })))
                );
            })
        )
    );

    toggleOcrEnabled$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.toggleOcrEnabled),
            mergeMap((action) => {
                return this.docAdminClient.toggleOcrEnabled().pipe(
                    map(() =>
                        DocumentTypeActions.DocumentTypes.toggleOcrEnabledSuccess({ isOcrToggleSuccessful: true })
                    ),
                    catchError((error) => {
                        return of(DocumentTypeActions.DocumentTypes.toggleOcrEnabledFailure({ error }));
                    })
                );
            })
        )
    );

    uploadDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.uploadDocument),
            mergeMap((action) =>
                this.mutateThenUpload(
                    this.docAdminClient.uploadDocument(action.document as unknown as DocumentTypeCreateDto),
                    action,
                    'Success Uploading Document'
                ).pipe(
                    map((finalDoc: AdminDocumentDto) =>
                        DocumentTypeActions.DocumentTypesDetail.uploadDocumentSuccess({
                            document: finalDoc as unknown as DocumentTypes
                        })
                    ),
                    catchError((error) => {
                        this.handleToast('Failure Uploading Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    getDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.getDocument),
            switchMap((action) =>
                this.docAdminClient.getDocumentType(action.documentId).pipe(
                    map((document: AdminDocumentDto) => {
                        this.handleToast('Success Getting Document');
                        return DocumentTypeActions.DocumentTypesDetail.getDocumentSuccess({
                            document: document as unknown as DocumentTypes
                        });
                    }),
                    catchError((error) => {
                        this.handleToast('Failure Getting Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.getDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    initializeDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.initializeDocument),
            switchMap((action) =>
                this.docAdminClient.initializeDocumentType().pipe(
                    map((document: AdminDocumentDto) =>
                        DocumentTypeActions.DocumentTypesDetail.initializeDocumentSuccess({
                            document: document as unknown as DocumentTypes
                        })
                    ),
                    catchError((error) => {
                        this.handleToast('Failure Getting Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.getDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    updateDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.updateDocument),
            mergeMap((action) =>
                this.mutateThenUpload(
                    this.docAdminClient.updateDocument(action.document as unknown as DocumentTypeUpdateDto),
                    action,
                    'Success Updating Document'
                ).pipe(
                    map((finalDoc: AdminDocumentDto) =>
                        DocumentTypeActions.DocumentTypesDetail.updateDocumentSuccess({
                            document: finalDoc as unknown as DocumentTypes
                        })
                    ),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Updating Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.updateDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    disableDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.disableDocument),
            mergeMap((action) =>
                this.mutateThenUpload(
                    this.docAdminClient.disableRequirement(action.document as unknown as DocumentTypeUpdateDto),
                    action,
                    'Disabled requirement successfully.'
                ).pipe(
                    map((finalDoc: AdminDocumentDto) =>
                        DocumentTypeActions.DocumentTypesDetail.disableDocumentSuccess({
                            document: finalDoc as unknown as DocumentTypes
                        })
                    ),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Disabling Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.disableDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    enableDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.enableDocument),
            mergeMap((action) =>
                this.mutateThenUpload(
                    this.docAdminClient.enableRequirement(action.document as unknown as DocumentTypeUpdateDto),
                    action,
                    'Re-enabled requirement successfully.'
                ).pipe(
                    map((finalDoc: AdminDocumentDto) =>
                        DocumentTypeActions.DocumentTypesDetail.enableDocumentSuccess({
                            document: finalDoc as unknown as DocumentTypes
                        })
                    ),
                    catchError((error) => {
                        const message = error.status === 400 ? error.error : 'Failure Enabling Document';
                        this.handleToast(message);
                        return of(DocumentTypeActions.DocumentTypesDetail.enableDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    uploadDocumentFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFile),
            switchMap((action) => {
                const file = action.documentFile.document;
                return this.docAdminClient
                    .uploadDocumentFile(action.documentId, { data: file, fileName: file.name })
                    .pipe(
                        map((document: AdminDocumentDto) => {
                            return DocumentTypeActions.DocumentTypesDetail.uploadDocumentFileSuccess({
                                document: document as unknown as DocumentTypes
                            });
                        }),
                        catchError((error) => {
                            this.handleToast('Failure Uploading Document');
                            return of(DocumentTypeActions.DocumentTypesDetail.uploadDocumentFileFailure({ error }));
                        })
                    );
            })
        )
    );

    uploadCandidateReviewDocumentFile$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.uploadCandidateReviewDocumentFile),
            switchMap((action) => {
                const file = action.candidateReviewDocumentFile.document;

                return this.docAdminClient
                    .uploadCandidateReviewDocumentFile(action.documentId, { data: file, fileName: file.name })
                    .pipe(
                        map((document: AdminDocumentDto) => {
                            return DocumentTypeActions.DocumentTypesDetail.uploadCandidateReviewDocumentFileSuccess({
                                document: document as unknown as DocumentTypes
                            });
                        }),
                        catchError((error) => {
                            this.handleToast('Failure Uploading Candidate Review Document');
                            return of(
                                DocumentTypeActions.DocumentTypesDetail.uploadCandidateReviewDocumentFileFailure({
                                    error
                                })
                            );
                        })
                    );
            })
        )
    );

    createNewDocument$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.createNewDocument),
            switchMap((action) =>
                this.docAdminClient.createPdf(action.documentId, action.pages).pipe(
                    map((document: AdminDocumentDto) => {
                        this.handleToast('Success Creating New Document');
                        return DocumentTypeActions.DocumentTypesDetail.createNewDocumentSuccess({
                            document: document as unknown as DocumentTypes
                        });
                    }),
                    catchError((error) => {
                        this.handleToast('Error Creating New Document');
                        return of(DocumentTypeActions.DocumentTypesDetail.createNewDocumentFailure({ error }));
                    })
                )
            )
        )
    );

    removeRequirement$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypesDetail.removeRequirement),
            switchMap((action) =>
                this.docAdminClient.removeRequirement(action.documentId).pipe(
                    map((document: AdminDocumentDto) => {
                        this.handleToast('Success Removing Requirement');
                        return DocumentTypeActions.DocumentTypesDetail.removeRequirementSuccess({
                            document: document as unknown as DocumentTypes
                        });
                    }),
                    catchError((error) => {
                        this.handleToast('Error Removing Requirement');
                        return of(DocumentTypeActions.DocumentTypesDetail.removeRequirementFailure({ error }));
                    })
                )
            )
        )
    );

    loadRequirementPermissions$ = createEffect(() =>
        this.actions$.pipe(
            ofType(DocumentTypeActions.DocumentTypes.getRequirementPermissions),
            switchMap(() => {
                return this.docAdminClient.getDocAdminRequirementPermissions().pipe(
                    map((permissions) =>
                        DocumentTypeActions.DocumentTypes.getRequirementPermissionsSuccess({ permissions })
                    ),
                    catchError((error) =>
                        of(DocumentTypeActions.DocumentTypes.getRequirementPermissionsFailure({ error }))
                    )
                );
            })
        )
    );

    refreshWinningRulesetAfterSave$ = createEffect(() =>
        this.actions$.pipe(
            ofType(
                DocumentTypeActions.DocumentTypesDetail.updateDocumentSuccess,
                DocumentTypeActions.DocumentTypesDetail.uploadDocumentSuccess
            ),
            switchMap((action) =>
                this.featureManager.isEnabled(MicDropFeatureFlags.EnableDocAdminAutoRefreshFix).pipe(
                    take(1),
                    filter(Boolean),
                    map(() => {
                        const document = action.document;
                        const documentTypeLevel = document.requirementLevelId as DocumentTypeLevelType;
                        const ruleExpressionLevelId = this.getRuleExpressionLevelId(document);
                        const dto: GetRuleExpressionsInHierarchyRequestDto = {
                            documentTypeId: document.id,
                            documentTypeLevel: documentTypeLevel,
                            ruleExpressionLevel: documentTypeLevel,
                            ruleExpressionLevelId: ruleExpressionLevelId,
                            includeLowerLevelExpressions: false,
                            acceptanceCriteriaStatusFilter: null
                        };
                        return loadAutomatchGroupedWinningRuleset({ getRuleExpressionsInHierarchyRequestDto: dto });
                    })
                )
            )
        )
    );

    private getRuleExpressionLevelId(document: DocumentTypes): number | null {
        switch (document.requirementLevelId) {
            case DocumentTypeLevel.State:
                return document.stateId;
            case DocumentTypeLevel.Facility:
                return document.facilityId;
            case DocumentTypeLevel.QmAssociation:
                return document.qmAssociationId;
            default:
                return document.id;
        }
    }

    handleToast(message: string) {
        this.toasterService.toast(message);
    }

    private getPendingUploads(action: any): { sample?: File; candidate?: File } {
        const sample = action?.documentFile?.document as File | undefined;

        const candidate =
            action?.document?.candidateTaskTypeId === 5
                ? (action?.candidateReviewDocumentFile?.document as File | undefined)
                : undefined;

        return {
            sample: !isEmpty(sample) ? sample : undefined,
            candidate: !isEmpty(candidate) ? candidate : undefined
        };
    }

    private uploadAnyPendingFiles(documentId: number, action: any): Observable<AdminDocumentDto> {
        const { sample, candidate } = this.getPendingUploads(action);

        const uploads: Array<Observable<AdminDocumentDto>> = [];

        if (sample) {
            uploads.push(this.docAdminClient.uploadDocumentFile(documentId, { data: sample, fileName: sample.name }));
        }

        if (candidate) {
            uploads.push(
                this.docAdminClient.uploadCandidateReviewDocumentFile(documentId, {
                    data: candidate,
                    fileName: candidate.name
                })
            );
        }

        // No files to upload
        if (uploads.length === 0) {
            // Important: caller should still return the "base" document dto from create/update/disable/enable
            return of(null as unknown as AdminDocumentDto);
        }

        // Upload one or both sequentially; last() returns the final dto from the last upload call
        return concat(...uploads).pipe(last());
    }

    /**
     * Helper to:
     *  - call the base mutation (create/update/disable/enable)
     *  - upload any pending files (0/1/2)
     *  - return the "final" document dto (prefer the upload response if uploads occurred, otherwise base)
     */
    private mutateThenUpload(
        base$: Observable<AdminDocumentDto>,
        action: any,
        successToast: string
    ): Observable<AdminDocumentDto> {
        return base$.pipe(
            switchMap((baseDoc: AdminDocumentDto) => {
                this.handleToast(successToast);

                return this.uploadAnyPendingFiles(baseDoc.id, action).pipe(
                    // if uploads happened, use last upload response; otherwise fall back to base doc
                    map((uploadedDoc) => uploadedDoc ?? baseDoc)
                );
            })
        );
    }
}
